import React, { FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Switch,
    FormControlLabel,
    FormControl,
    RadioGroup,
    Radio,
    Typography,
} from '@mui/material';
import { LabClient, ClSettings } from 'models/client-directory';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyClientLocaleFormatTemplate, saveSettingsTemplate } from 'apollo/templates/client-directory';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import { emailRegExp } from 'config/regex';
import LocaleFormatPicker from 'components/shared/locale-format-picker/locale-format-picker.component';
import { DEFAULT_LOCALE_FORMAT, LocaleFormat } from 'models/locale-format';

type SettingsProps = {
    labClient: LabClient;
    onSettingsSaved?: () => void;
};

type EmailNotifications = {
    type: 'dentist' | 'shared';
    sharedEmails: string[];
};

const Settings: FC<SettingsProps> = ({ labClient, onSettingsSaved }: SettingsProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'lab-settings']);
    const [settings, setSettings] = useState<ClSettings>({} as ClSettings);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isEmailFieldValid, setIsEmailFieldValid] = useState(true);
    const [emailNotifications, setEmailNotifications] = useState<EmailNotifications>({
        type: 'dentist',
        sharedEmails: [],
    });
    const [localeFormatEnabled, setLocaleFormatEnabled] = useState(false);
    const [localeFormat, setLocaleFormat] = useState<LocaleFormat>(DEFAULT_LOCALE_FORMAT);

    useEffect(() => {
        if (labClient.settings) {
            setSettings(labClient.settings);
            setEmailNotifications({
                type: labClient.settings.deliveryNotesEmailType || 'dentist',
                sharedEmails: labClient.settings.sharedEmails || [],
            });
        }
    }, [labClient.settings]);

    useEffect(() => {
        if (labClient.localeFormat) {
            setLocaleFormatEnabled(true);
            setLocaleFormat(labClient.localeFormat);
        } else {
            setLocaleFormatEnabled(false);
            setLocaleFormat(DEFAULT_LOCALE_FORMAT);
        }
    }, [labClient.localeFormat]);

    const [saveSettings] = useMutation(saveSettingsTemplate, {
        onError: (error) => {
            showBoundary(error);
        },
    });

    const [modifyClientLocaleFormat] = useMutation(modifyClientLocaleFormatTemplate, {
        onError: (error) => {
            showBoundary(error);
        },
    });

    const handleSaveSettings = async () => {
        await Promise.all([
            saveSettings({
                variables: {
                    clientId: labClient.customer._id,
                    settings: {
                        sendDeliveryNotesByEmail: settings.sendDeliveryNotesByEmail,
                        deliveryNotesEmailType: emailNotifications.type,
                        sharedEmails: emailNotifications.sharedEmails,
                    },
                },
            }),
            modifyClientLocaleFormat({
                variables: {
                    clientId: labClient.customer._id,
                    input: localeFormatEnabled ? localeFormat : null,
                },
            }),
        ]);
        if (onSettingsSaved) {
            onSettingsSaved();
        }
    };

    const toggleDeliveryNotesByEmail = (isChecked: boolean) => {
        setSettings({
            ...settings,
            sendDeliveryNotesByEmail: isChecked,
        });
    };

    const handleRadioType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailNotifications({
            ...emailNotifications,
            type: event.target.value as 'dentist' | 'shared',
        });
    };

    const handleFieldChange = (name: string, value: string[]) => {
        setEmailNotifications({
            ...emailNotifications,
            [name]: value,
        });
    };

    const validateEmail = (value: string) => {
        return emailRegExp.test(String(value).toLowerCase());
    };

    const handleToError = (err: Error | null) => {
        setIsEmailFieldValid(!err);
    };

    const disableSave = () => {
        if (settings.sendDeliveryNotesByEmail && emailNotifications.type === 'shared' && !isEmailFieldValid) {
            return true;
        }
        if (
            settings.sendDeliveryNotesByEmail &&
            emailNotifications.type === 'shared' &&
            emailNotifications.sharedEmails.length === 0
        ) {
            return true;
        }
        return false;
    };

    return (
        <Paper>
            <Box p={2}>
                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                color="secondary"
                                checked={settings.sendDeliveryNotesByEmail}
                                onChange={(_, value) => toggleDeliveryNotesByEmail(value)}
                            />
                        }
                        label={translate('client-directory:SEND_DELIVERY_NOTES_BY_EMAIL')}
                    />
                </Box>
                {settings.sendDeliveryNotesByEmail && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            {translate('lab-settings:EMAIL_FOR_NOTIFICATIONS')}
                        </Typography>
                        <FormControl fullWidth>
                            <RadioGroup value={emailNotifications.type} onChange={handleRadioType}>
                                <FormControlLabel
                                    value="dentist"
                                    control={<Radio />}
                                    label="Send to the dentist e-mail"
                                />

                                <FormControlLabel
                                    value="shared"
                                    control={<Radio />}
                                    label="Send to a shared e-mail regardless of dentist's email"
                                />
                            </RadioGroup>
                            {emailNotifications.type === 'shared' && (
                                <Box ml={4} mt={1}>
                                    <ChipInput
                                        required
                                        label={translate('lab-settings:EMAIL_ADDRESSES')}
                                        placeholder={translate('lab-settings:ENTER_EMAIL_ADDRESS')}
                                        options={[]}
                                        createChipOnBlur
                                        chipValidator={validateEmail}
                                        onError={handleToError}
                                        defaultValue={emailNotifications.sharedEmails || []}
                                        onValueChange={(value: string[]) => handleFieldChange('sharedEmails', value)}
                                        onDelete={(value: string[]) => handleFieldChange('sharedEmails', value)}
                                    />
                                </Box>
                            )}
                        </FormControl>
                    </Box>
                )}
                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                color="secondary"
                                checked={localeFormatEnabled}
                                onChange={(_, value) => setLocaleFormatEnabled(value)}
                            />
                        }
                        label={translate('client-directory:SET_LOCALE_FORMAT_FOR_CUSTOMER')}
                    />
                    {localeFormatEnabled && (
                        <Box mt={2}>
                            <LocaleFormatPicker value={localeFormat} onChange={setLocaleFormat} />
                        </Box>
                    )}
                </Box>
                <Box mt={2}>
                    <Button variant="contained" onClick={handleSaveSettings} disabled={disableSave()}>
                        {translate('common:SAVE')}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default Settings;
